import React, { useState, useMemo, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  MagnifyingGlass,
  Rocket,
  Robot,
  ChartLineUp,
  BookOpen,
  Warning,
  CaretDown,
  CaretUp,
  ChatCircle,
  Sparkle,
} from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL;

const ICON_MAP = {
  Rocket,
  Robot,
  ChartLineUp,
  BookOpen,
  Warning,
  Sparkle,
};

// Very light markdown → HTML (bold + line-breaks). Safe since content is ours.
function renderSimpleMarkdown(text = '') {
  const escaped = text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
  return escaped
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\n/g, '<br/>');
}

export default function HelpPage() {
  const [categories, setCategories] = useState([]);
  const [query, setQuery] = useState('');
  const [openArticles, setOpenArticles] = useState(new Set());
  const [loading, setLoading] = useState(true);

  const getHeaders = useCallback(() => ({ Authorization: `Bearer ${localStorage.getItem('token')}` }), []);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/api/help/faq`, { headers: getHeaders() });
        setCategories(data.categories || []);
      } catch (err) {
        // ignore; page still shows
      } finally { setLoading(false); }
    })();
  }, [getHeaders]);

  const filtered = useMemo(() => {
    if (!query.trim()) return categories;
    const q = query.toLowerCase();
    return categories
      .map((cat) => ({
        ...cat,
        articles: cat.articles.filter(
          (a) =>
            a.question.toLowerCase().includes(q) ||
            a.answer.toLowerCase().includes(q) ||
            (a.tags || []).some((t) => t.toLowerCase().includes(q)),
        ),
      }))
      .filter((c) => c.articles.length > 0);
  }, [categories, query]);

  const toggleArticle = (id) => {
    const next = new Set(openArticles);
    if (next.has(id)) next.delete(id); else next.add(id);
    setOpenArticles(next);
  };

  const totalArticles = categories.reduce((n, c) => n + c.articles.length, 0);

  return (
    <div className="space-y-6 max-w-4xl" data-testid="help-page">
      <div>
        <h1 className="font-heading text-3xl sm:text-4xl tracking-tight font-extrabold">Hjelp & FAQ</h1>
        <p className="text-sm text-muted-foreground mt-1">
          {totalArticles} artikler som dekker det meste.{' '}
          Finner du ikke svaret? <button
            onClick={() => window.dispatchEvent(new CustomEvent('open-tilly'))}
            className="text-primary hover:underline"
            data-testid="open-tilly-from-faq"
          >
            Spør Tilly <ChatCircle size={12} className="inline" />
          </button>
        </p>
      </div>

      <div className="relative">
        <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input
          placeholder="Søk i FAQ – f.eks. 'CTR', 'AutoGuard', 'bilder'…"
          className="pl-10"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          data-testid="faq-search-input"
        />
      </div>

      {loading ? (
        <div className="h-64 bg-muted animate-pulse rounded-md" />
      ) : filtered.length === 0 ? (
        <Card className="border border-border">
          <CardContent className="p-8 text-center">
            <p className="text-sm text-muted-foreground mb-4">
              Ingen artikler matchet "{query}". Prøv et annet søk, eller spør Tilly direkte.
            </p>
            <button
              onClick={() => window.dispatchEvent(new CustomEvent('open-tilly', { detail: { message: query } }))}
              className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90"
              data-testid="ask-tilly-empty"
            >
              <ChatCircle size={14} /> Spør Tilly om "{query}"
            </button>
          </CardContent>
        </Card>
      ) : (
        filtered.map((cat) => {
          const Icon = ICON_MAP[cat.icon] || BookOpen;
          return (
            <div key={cat.id} className="space-y-2">
              <div className="flex items-center gap-2 mt-4">
                <Icon size={20} weight="bold" className="text-primary" />
                <h2 className="font-heading text-xl font-bold">{cat.title}</h2>
                <Badge variant="secondary" className="text-xs">{cat.articles.length}</Badge>
              </div>
              <div className="space-y-2">
                {cat.articles.map((art) => {
                  const open = openArticles.has(art.id);
                  return (
                    <Card key={art.id} className="border border-border transition-colors hover:border-primary/50">
                      <button
                        onClick={() => toggleArticle(art.id)}
                        className="w-full text-left p-4 flex items-center justify-between gap-3"
                        data-testid={`faq-article-${art.id}`}
                      >
                        <span className="font-medium">{art.question}</span>
                        {open ? <CaretUp size={16} /> : <CaretDown size={16} />}
                      </button>
                      {open && (
                        <div className="px-4 pb-4 -mt-1">
                          <div
                            className="text-sm text-muted-foreground leading-relaxed prose prose-sm max-w-none prose-strong:text-foreground"
                            dangerouslySetInnerHTML={{ __html: renderSimpleMarkdown(art.answer) }}
                          />
                          <div className="flex gap-1 mt-3 flex-wrap">
                            {(art.tags || []).map((t) => (
                              <Badge key={t} variant="outline" className="text-xs text-muted-foreground">#{t}</Badge>
                            ))}
                          </div>
                        </div>
                      )}
                    </Card>
                  );
                })}
              </div>
            </div>
          );
        })
      )}
    </div>
  );
}
